import cn from 'classnames';
import type { GetStaticPropsContext, GetStaticPropsResult } from 'next';
import { ReactNode } from 'react';
import router, { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import Cookies from 'js-cookie';

import { CHECKOUT_STATUSES } from '@adapters/bigcommerce/constants';
import { useCustomer } from '@adapters/bigcommerce/hooks/customer';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { MyStoreData } from '@adapters/prismic/fetching/stores/types';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';

import { AccountTabs } from '@components/account/types';
import LoginForm from '@components/auth/LoginForm';
import Breadcrumbs from '@components/global/Breadcrumbs';
import Container from '@components/global/Container';
import Layout from '@components/global/Layout';
import Button from '@components/ui/Button';
import Divider from '@components/ui/Divider';
import Heading from '@components/ui/Heading';

import {
  FEATURE_FLAG_GUEST_CHECKOUT,
  FEATURE_FLAG_LOYALTY,
  FEATURE_FLAG_PROMO_BAR_SERVER_SIDE,
  loyaltyFFCookieName,
} from '@config/feature-flag';

import { useCartUI } from '@context/CartContext';

import { useLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';

import { buildCheckoutCustomerStatusQuery } from '@utils/analytics/buildCheckoutCustomerStatusQuery';
import { MEC_TITLE } from '@utils/constants';
import { trackCartViewEvent, GA4_EVENT_NAMES } from '@utils/gtm/ga4';
import checkoutAnalytics from '@utils/gtm/ga4/checkout';
import { altLocale } from '@utils/locale';
import { localeAltPathAbs, localePathAbs } from '@utils/url';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';

import styles from './signin.module.scss';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';

const { SIGN_IN_AT_CHECKOUT } = GA4_EVENT_NAMES;
interface SignInPageProps {
  canonicalUrl: string;
  langAltUrl: string;
  promoMessages?: PromoTicker[] | null;
  xDefault: string;
}

interface MECStores {
  stores: MyStoreData[] | null;
}

export async function getStaticPaths() {
  return {
    paths: [{ params: { locale: 'en' } }, { params: { locale: 'fr' } }],
    fallback: false,
  };
}

export async function getStaticProps({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<
  SignInPageProps | MECStores
> | null> {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const previewRef = getPreviewRef(preview, previewData);

  const [
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    promoBarData,
  ] = await Promise.allSettled([
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    serverSideTranslations(locale, ['common', 'auth', 'checkout']),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  const canonicalUrl = localePathAbs(locale, `/auth/signin`) ?? '';
  const langAltUrl = localeAltPathAbs(locale, `/auth/signin`) ?? '';
  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  return {
    props: {
      stores,
      canonicalUrl,
      langAltUrl,
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      promoBarData,
      bannerStripData,
    },
  };
}

const Signup = ({ loyaltyFeatureFlag }: { loyaltyFeatureFlag: boolean }) => {
  const { t } = useTranslation('common');
  const { query } = useRouter();
  const locale = (query.locale as string) || 'en';

  return (
    <div className={styles.signUpForm}>
      <Heading styleLevel="3" semanticLevel="h2">
        {loyaltyFeatureFlag
          ? t('checkout.member.signup.title.loyalty')
          : t('checkout.member.signup.title')}
      </Heading>
      <p>
        {loyaltyFeatureFlag
          ? t('checkout.member.signup.body.loyalty')
          : t('checkout.member.signup.body')}
      </p>
      <Button
        href={`/${locale}/auth/register?redirect=${
          query.redirect ?? 'checkout'
        }${query?.queryID ? `&queryID=${query.queryID}` : ''}`}
        variant="secondary"
        label={t('checkout.member.signup.button')}
        size="large"
        className={styles.button}
        handleClick={() => checkoutAnalytics.clickSignUp()}
      />
    </div>
  );
};

export default function SignInPage({
  canonicalUrl,
  langAltUrl,
  xDefault,
}: SignInPageProps): ReactNode {
  const { query } = useRouter();
  const locale = (query.locale as string) || 'en';
  const { redirect, displayLogin } = query || {};

  const accountTabs = Object.values(AccountTabs);
  const isAccountPage = accountTabs.includes(redirect as AccountTabs);

  const isCheckout = redirect === 'checkout';

  const { t } = useTranslation('common');
  const { userLoggedIn } = useCustomer();

  const { cart } = useCartUI();

  const [gcEnabledCookie, setGcEnabledCookie] = useState(
    // Note we will be removing after guest checkout go live
    false
  );

  const [loyaltyFeatureFlag, setLoyaltyFeatureFlag] =
    useState(FEATURE_FLAG_LOYALTY);

  useLayoutEffect(() => {
    const loyaltyCookieValue = Cookies.get(loyaltyFFCookieName);
    if (loyaltyCookieValue === 'true') {
      setLoyaltyFeatureFlag(true);
    }
  }, []);

  useLayoutEffect(() => {
    // Note we will be removing after guest checkout go live
    const gcEnabledCookieValue = Cookies.get('mec_guestCheckoutEnabled');
    if (gcEnabledCookieValue === 'true') {
      setGcEnabledCookie(true);
    }
  }, []);

  const isGuestCheckoutEnabled = gcEnabledCookie || FEATURE_FLAG_GUEST_CHECKOUT; // Note - we'll keep the feature flag after go live but can clean up the cookie check

  const description = t('login.description');
  const title = t('login.sign.in');

  const onLogin = () => {
    const redirectToMyAccount = () => {
      window.location.href = `/${locale}/my-account?p=${redirect}${
        query?.token ? `&token=${query.token}` : ''
      }`;
    };

    const redirectToCheckout = () => {
      checkoutAnalytics.accountLoginBeforeCheckout();
      trackCartViewEvent({
        eventName: SIGN_IN_AT_CHECKOUT,
        cart,
      });

      const queryWithMemberStatus = buildCheckoutCustomerStatusQuery({
        status: CHECKOUT_STATUSES.MEMBER_LOGGED_OUT,
        query,
        excludeKeys: ['redirect', 'displayLogin', 'token'],
      });
      router.push(`/${locale}/checkout${queryWithMemberStatus}`);
    };

    const redirectToHome = () => {
      router.push('/');
      userLoggedIn();
    };

    if (isAccountPage) {
      redirectToMyAccount();
    } else if (redirect === 'checkout') {
      redirectToCheckout();
    } else {
      redirectToHome();
    }
  };

  const onOpenLoginForm = () => {
    checkoutAnalytics.clickSignIn();
    router.push({
      pathname: router.pathname,
      query: { ...router.query, displayLogin: true },
    });
  };

  const onGuestClick = () => {
    checkoutAnalytics.clickGuest();

    const queryWithMemberStatus = buildCheckoutCustomerStatusQuery({
      status: CHECKOUT_STATUSES.GUEST,
      query,
      excludeKeys: ['redirect', 'displayLogin'],
    });

    router.push(`/${locale}/checkout${queryWithMemberStatus}`);
  };

  return (
    <>
      <NextSeo
        noindex
        description={description}
        title={`${title} ${MEC_TITLE}`}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={{
          title,
          description,
          locale,
        }}
      />
      <Container>
        <Breadcrumbs
          name={
            !isGuestCheckoutEnabled
              ? t('login.sign.in')
              : !displayLogin
              ? t('checkout.page.breadcrumb.landing')
              : t('checkout.page.breadcrumb.signin')
          }
          breadcrumbs={[]}
        />
        <div
          className={cn(styles.signinContainer, {
            [styles.guestCheckoutEnabled]: isGuestCheckoutEnabled,
          })}
        >
          {isGuestCheckoutEnabled ? (
            <>
              <div className={styles.leftCol}>
                <Heading
                  styleLevel="3"
                  semanticLevel="h1"
                  className={styles.heading}
                >
                  {displayLogin || !isCheckout
                    ? t('checkout.member.continue.title')
                    : t('checkout.page.title')}
                </Heading>
                {displayLogin && (
                  <>
                    <LoginForm
                      onLogin={onLogin}
                      destination={isCheckout ? 'checkout' : 'default'}
                    />
                    <Divider
                      className={cn(styles.hr, {
                        [styles.hide]: !displayLogin,
                      })}
                    />
                  </>
                )}
                {!displayLogin && (
                  <>
                    <Button
                      handleClick={onOpenLoginForm}
                      variant="primary"
                      label={
                        redirect === 'checkout'
                          ? t('checkout.member.continue')
                          : redirect === AccountTabs.MemberRewards &&
                            loyaltyFeatureFlag
                          ? t('login.sign.in.loyalty')
                          : t('login.sign.in')
                      }
                      size="large"
                      className={cn(styles.button, styles.memberCheckoutButton)}
                    />
                    {isCheckout && (
                      <Button
                        variant="tertiary"
                        label={t('checkout.guest.continue')}
                        size="large"
                        className={cn(
                          styles.button,
                          styles.guestCheckoutButton,
                          styles.signInStateNotActive
                        )}
                        handleClick={onGuestClick}
                      />
                    )}
                  </>
                )}
              </div>

              <Divider
                className={cn(styles.hr, { [styles.hide]: displayLogin })}
              />
              <div className={styles.rightCol}>
                {displayLogin && isCheckout && (
                  <>
                    <Heading
                      styleLevel="3"
                      semanticLevel="h2"
                      className={styles.guestCheckoutHeading}
                    >
                      {t('checkout.guest.continue.title')}
                    </Heading>

                    <Button
                      href={`/${locale}/checkout`}
                      variant="tertiary"
                      label={t('checkout.guest.continue')}
                      size="large"
                      className={cn(
                        styles.button,
                        styles.guestCheckoutButton,
                        styles.signInStateActive
                      )}
                    />
                  </>
                )}

                <Signup loyaltyFeatureFlag={loyaltyFeatureFlag} />
              </div>
            </>
          ) : (
            // This is the old checkout, can be removed once we go live with guest checkout, unless we want to keep it around for a fallback?
            <>
              <Heading styleLevel="3" semanticLevel="h1">
                {t('login.sign.in')}
              </Heading>
              <LoginForm
                onLogin={onLogin}
                destination={isCheckout ? 'checkout' : 'default'}
              />
            </>
          )}
        </div>
      </Container>
    </>
  );
}

SignInPage.Layout = Layout;
